import {User} from "@/shared/timr";
import {Combobox} from "@/shared/ui/combobox";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {ProjectTimeDialog} from "@/features/projecttime/projectTimeDialog";
import {TaskInfoDialog} from "@/features/task/taskInfoDialog";
import {BaseTimelinePage, TimelineConfig} from "./baseTimelinePage";
import {WorkingTimeTypeFilter} from "@/features/timeline/timelineTypes";
import {ReportingQueryParams} from "@/shared/queryParams";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import {parseDateInput, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {ReportViewType, TimelineMode} from "@/shared/timrTypes";

interface TimelineReportConfig extends TimelineConfig {
	users: User[];
}

/**
 * Personal timeline for the use of the timeline just for the current user.
 * Contains date filter input for a single day. As well as user if the current user has teamleader permission, if it behaves basically like the personal TimelineDay.
 * Filter input values are stored in session storage (independent of timelinePersonal).
 */
export class TimelineReportPage extends BaseTimelinePage {
	private userCombobox: Combobox<User>;

	constructor(public config: TimelineReportConfig) {
		// the initial date and timeline mode are determined based on query parameters or session settings. if a query parameter is specified, it takes precedence over the cached session setting.
		// e.g., a link of reports/recordingValidations to this timeline view
		const initialTimelineDate: Temporal.PlainDate = ReportingQueryParams.get("date")
			?? BrowserStorageSettings.getTimelineDate(BrowserStorageSettings.GROUP_TIMELINE_REPORT)
			?? Temporal.Now.plainDateISO();

		let initialTimelineMode = BrowserStorageSettings.getTimelineMode(BrowserStorageSettings.GROUP_TIMELINE_REPORT);
		const viewType = ReportingQueryParams.get("viewType");
		if (viewType) {
			initialTimelineMode = viewType === ReportViewType.WEEKLY ? TimelineMode.Weekly : TimelineMode.Daily;
		}

		super(config, initialTimelineDate, initialTimelineMode, BrowserStorageSettings.getTimelineSortDescending(BrowserStorageSettings.GROUP_TIMELINE_REPORT));
		this.initDialogs();

		// only initialize user selection if current user has permission
		if (globals.isGroupLeader) {
			this.initUserCombobox();

			const userId = ReportingQueryParams.get("userId");
			if (userId) {
				const user = this.getUser(userId);
				if (user) {
					this.userCombobox.setSelectedItem(user, false, false, false);
				}
			}
		}
		this.onInputChanged();
	}

	/**
	 * Initialize dialogs for editing working and project times in timeline report. User selection is visible for team leaders.
	 * @protected
	 */
	protected initDialogs(): void {
		this.workingTimeDialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			customFieldDefinitions: this.config.customFieldDefinitionsWorkingTime,
			defaultStatusNumber: this.config.defaultStatusNumberWorkingTime,
			userSelectionEnabled: globals.isGroupLeader,
			users: this.config.users,
			allowedUserIds: this.config.users.map(u => u.id),
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.workingTimeDateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.workingTimeDateTimeChangeableByUserDayRangeMode
		});
		this.projectTimeDialog = new ProjectTimeDialog({
			dialogId: "#projectTimeDialog",
			customFieldDefinitions: this.config.customFieldDefinitionsProjectTime,
			defaultStatusNumber: this.config.defaultStatusNumberProjectTime,
			userSelectionEnabled: globals.isGroupLeader,
			users: this.config.users,
			allowedUserIds: this.config.users.map(u => u.id),
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.projectTimeDateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.projectTimeDateTimeChangeableByUserDayRangeMode
		});
		this.initDialogListeners();
	}

	/**
	 * Triggers reload of timeline with selected user and date (start=end)
	 * If user is no group leader/multiuser user selection current user is used (user combobox is hidden)
	 */
	private reloadTimeline(): void {
		this.timelineComponent.load(this.getSelectedReportUser()?.id ?? globals.userId, this.getTimelineDate());
	}

	/**
	 * Update sessionSettings variables and reloads timeline
	 */
	protected onInputChanged(): void {
		// update session storage variables for filter inputs
		BrowserStorageSettings.setTimelineDate(BrowserStorageSettings.GROUP_TIMELINE_REPORT, parseDateInput(this.dateInput.value) ?? null);
		BrowserStorageSettings.setTimelineMode(BrowserStorageSettings.GROUP_TIMELINE_REPORT, this.modeControl.getValue());
		BrowserStorageSettings.setTimelineSortDescending(BrowserStorageSettings.GROUP_TIMELINE_REPORT, this.isSortDescending());

		// save selected user id, if permission is given and user selection available
		if (globals.isGroupLeader && this.userCombobox) {
			BrowserStorageSettings.setTimelineReportUserId(this.userCombobox.getSelectedItem().id);
		}
		this.updateTodayButtonState();
		this.reloadTimeline();
	}

	/**
	 * Opens add working time dialog (or add absence/vacation dialog) <p>
	 * For this purpose, the date fields are preselected by the provided timestamps as well as working time types
	 * By the provided method {@link workingTimeTypeFilter} which filters all types depending on which ones are required (absence or working time)
	 * If teamleader the selected user (combobox) will be preselected, otherwise current user (no user select in dialog)
	 *
	 * @param workingTimeTypeFilter filter function to filter working time types, e.g. depending on whether add wt/absence
	 * @param start date time which should be preselected in the dialog
	 * @param end date time which should be preselected in the dialog
	 * @private
	 */
	protected onAddWorkingTime(workingTimeTypeFilter: WorkingTimeTypeFilter, start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		// filter all working time types (different dependent of add working time or absence)
		const workingTimeTypes = workingTimeTypeFilter(this.config.workingTimeTypes);

		// use user from combobox if team leader otherwise current user
		const user = this.getSelectedReportUser();

		// clear group dropdown before opening the dialog to ensure the desired user is selected (no group selected and user dropdown contains user)
		this.workingTimeDialog.clearGroup();

		// open working time dialog with selected user working time types and start/end times
		this.workingTimeDialog.create({userId: user?.id}, false, workingTimeTypes);  // createAnother=false to hide delete button
		this.workingTimeDialog.setStartEndTime(start, end);
	}

	/**
	 * Opens add project time dialog (or add absence/vacation dialog) <p>
	 * For this purpose, the date fields are preselected by the provided timestamps
	 * If team leader the selected user (combobox) will be preselected, otherwise current user (no user select in dialog)
	 *
	 * @param start date time which should be preselected in the dialog
	 * @param end date time which should be preselected in the dialog
	 * @private
	 */
	protected onAddProjectTime(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		// if team leader use selected user from combobox otherwise current user
		const user = this.getSelectedReportUser();

		// clear group dropdown to ensure the desired user is selected (no group selected and user dropdown contains user)
		this.projectTimeDialog.clearGroup();

		// open project time dialog with selected user
		this.projectTimeDialog.create({userId: user?.id});

		// select start and end time
		this.projectTimeDialog.setStartEndTime(start, end);
	}

	/**
	 * Opens the add working time notice dialog with the shown user preselected. <p>
	 * If team leader the selected user (combobox) is preselected, otherwise the current user. The dialog shows the
	 * user field (reportingOtherUsers), so the preselected user is visible and can be changed.
	 *
	 * @param start start date preselected in the dialog
	 * @param end end date preselected in the dialog
	 */
	protected override onAddWorkingTimeNotice(start: Temporal.PlainDate | null, end: Temporal.PlainDate | null): void {
		// use user from combobox if team leader otherwise current user
		const user = this.getSelectedReportUser();

		this.workingTimeNoticeDialog.openForCreate({
			start,
			end,
			selectedUser: user ? {id: user.id, fullname: user.fullname} : null
		});
	}

	protected initTaskInfoDialog(): void {
		this.taskInfoDialog = new TaskInfoDialog({
			dialogId: "#taskInfoDialog"
		});
	}

	private initUserCombobox(): void {
		this.userCombobox = new Combobox({
			items: this.config.users,
			formatter: user => user.fullname,
			idElement: $("#timelineReportFilterUserId"),
			filterElement: $("#timelineReportFilterUserFilter"),
			caretElement: $("#timelineReportFilterUserCaret"),
			clearable: false // not clearable because a user has to be selected in timeline report
		});
		// select stored user with current user as fallback
		this.userCombobox.setSelectedItem(this.getUser(BrowserStorageSettings.getTimelineReportUserId()) ?? this.getUser(globals.userId));
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, () => this.onInputChanged());
	}

	private getSelectedReportUser(): User | undefined {
		return globals.isGroupLeader ? this.userCombobox?.getSelectedItem() : this.getUser(globals.userId);
	}

	private getUser(userId: number): User | undefined {
		return this.config.users.find(u => u.id === userId);
	}
}
